/**
 * mandateNavConfig.ts — nav config for the Mandate app mobile shell.
 *
 * Mirrors MandateSidebar.tsx, collapsed into 4 tabs + a More drawer.
 */
import {
  Home,
  ListOrdered,
  ScrollText,
  Library,
  Menu,
  Target,
  Users,
  Compass,
  PlayCircle,
  Wrench,
  GitBranch,
  Settings,
} from 'lucide-react';
import { MORE_SENTINEL, type MobileNavItem, type MobileNavSection } from './navConfig';

export const MANDATE_TAB_ITEMS: MobileNavItem[] = [
  { label: 'Home', href: '/apps/mandate/dashboard', icon: Home },
  { label: 'Priorities', href: '/apps/mandate/priorities', icon: ListOrdered },
  { label: 'Decisions', href: '/apps/mandate/decisions', icon: ScrollText },
  { label: 'Items', href: '/apps/mandate/items', icon: Library },
  { label: 'More', href: MORE_SENTINEL, icon: Menu },
];

export const MANDATE_DRAWER_SECTIONS: MobileNavSection[] = [
  {
    label: 'Transition',
    items: [
      { label: 'Day 100 Outcome', href: '/apps/mandate/outcome', icon: Target },
      { label: 'Stakeholders', href: '/apps/mandate/stakeholders', icon: Users },
      { label: 'Situation', href: '/apps/mandate/situation', icon: Compass },
    ],
  },
  {
    label: 'Work',
    items: [
      { label: 'Sessions', href: '/apps/mandate/sessions', icon: PlayCircle },
      { label: 'Tools', href: '/apps/mandate/tools', icon: Wrench },
      { label: 'Lineage', href: '/apps/mandate/lineage', icon: GitBranch },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Transition Setup', href: '/apps/mandate/setup', icon: Settings },
    ],
  },
];

const ALL_MANDATE_ITEMS: MobileNavItem[] = [
  ...MANDATE_TAB_ITEMS.filter((t) => t.href !== MORE_SENTINEL),
  ...MANDATE_DRAWER_SECTIONS.flatMap((s) => s.items),
];

/** Tab destinations that hide the fixed title bar. */
export const MANDATE_TOP_LEVEL_PATHS = new Set([
  '/apps/mandate',
  '/apps/mandate/dashboard',
  '/apps/mandate/priorities',
  '/apps/mandate/decisions',
  '/apps/mandate/items',
]);

export function isMandateTopLevelPath(pathname: string): boolean {
  return MANDATE_TOP_LEVEL_PATHS.has(pathname);
}

/** Which tab should appear active for a given mandate path. */
export function activeMandateTabHref(pathname: string): string | null {
  const candidates = MANDATE_TAB_ITEMS.filter((t) => t.href !== MORE_SENTINEL);
  const match = candidates
    .filter(
      (t) =>
        pathname === t.href ||
        pathname.startsWith(t.href + '/') ||
        pathname.startsWith(t.href + '?'),
    )
    .sort((a, b) => b.href.length - a.href.length)[0];
  return match?.href ?? null;
}

/** Best-effort title for a mandate route. */
export function resolveMandateTitle(pathname: string): string {
  if (/^\/apps\/mandate\/items\/[^/]+$/.test(pathname)) return 'Item';
  if (/^\/apps\/mandate\/sessions\/[^/]+$/.test(pathname)) return 'Session';

  const match = ALL_MANDATE_ITEMS
    .filter(
      (i) =>
        pathname === i.href ||
        pathname.startsWith(i.href + '/') ||
        pathname.startsWith(i.href + '?'),
    )
    .sort((a, b) => b.href.length - a.href.length)[0];
  if (match) return match.label;

  const seg = pathname.split('?')[0].split('/').filter(Boolean).pop() ?? 'Mandate';
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
